import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  AuthState,
  AuthStateData,
  CacheService,
  DialogService,
  environment,
  PasswordService,
  RegistrationService,
  TranslateService,
} from '@gb-webapp/gb-app';
import { from, map, Observable, of, switchMap, tap } from 'rxjs';
import { AUTH_ROUTES, includesAuthRoute } from './auth-routes.constants';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ReferralCodeComponent } from './components/referral-code/referral-code.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ResetSuccessComponent } from './components/reset-success/reset-success.component';
import { SignInComponent } from './components/sign-in/sign-in.component';
import { SignUpComponent } from './components/sign-up/sign-up.component';
import { VerifyAccountComponent } from './components/verify-account/verify-account.component';
@Component({
  selector: 'app-auth',
  imports: [
    SignUpComponent,
    SignInComponent,
    ForgotPasswordComponent,
    VerifyAccountComponent,
    ResetPasswordComponent,
    ResetSuccessComponent,
    ReferralCodeComponent,
  ],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
})
export class AuthComponent implements OnInit {
  selectedState = signal<AuthState>(AuthState.SIGN_UP);

  stateData: AuthStateData = {};

  startUpState = input<AuthState>(AuthState.SIGN_UP);
  source = input<string>('');

  postAuthRedirect = input<{ url?: string; queryParams?: { [key: string]: string } }>({});

  autoRedirectOnSocialLogin = input<boolean>(false);

  // Make AuthState accessible in template
  readonly AuthState = AuthState;

  private readonly authService = inject(AuthService);

  private readonly dialogService = inject(DialogService);

  private readonly router = inject(Router);

  protected readonly registrationService = inject(RegistrationService);

  protected readonly translateService = inject(TranslateService);

  protected cacheService = inject(CacheService);

  public logoImage = computed<string>(() => {
    const cachedContent = this.cacheService.cachedContent();
    const webAppConfig = cachedContent?.cache?.config?.webAppConfig;

    if (environment.previewMode && webAppConfig?.logoPreview) {
      return webAppConfig.logoPreview;
    }

    return webAppConfig?.logo || './assets/images/logo.svg';
  });

  public successMessage = signal<string>(
    'Check your inbox for an email with your verification code.'
  );

  public validationErrorMessage = signal<string>('');

  public signupRequestInfo = signal<{ email: string; signupRequestId: string }>({
    email: '',
    signupRequestId: '',
  });

  public resetPasswordOtp = signal<{ email: string; otp: string }>({ email: '', otp: '' });

  protected readonly passwordService = inject(PasswordService);

  private isUpdatingUrl = false;

  constructor() {
    // Watch for state changes and update URL when switching between SIGN_IN and SIGN_UP
    effect(() => {
      const state = this.selectedState();
      // Only update URL for sign-in and sign-up states, and only if we're not already updating
      if (this.isUpdatingUrl || (state !== AuthState.SIGN_IN && state !== AuthState.SIGN_UP)) {
        return;
      }

      try {
        const targetPath = state === AuthState.SIGN_IN ? AUTH_ROUTES.SIGN_IN : AUTH_ROUTES.SIGN_UP;
        const currentUrl = this.router.url || globalThis.location?.pathname || '';

        // Update URL if we need to change to a different path
        if (currentUrl.includes(targetPath)) {
          return;
        }

        this.isUpdatingUrl = true;
        // Use history API to update URL without triggering navigation
        globalThis.history?.replaceState({}, '', targetPath);
        this.isUpdatingUrl = false;
      } catch (error) {
        // Handle any navigation errors (e.g., during SSR)
        console.error('Error updating URL:', error);
        this.isUpdatingUrl = false;
      }
    });
  }

  ngOnInit(): void {
    this.selectedState.set(this.startUpState());
  }

  // Method to close the auth modal
  closeModalWithData(): void {
    this.dialogService.closeTopDialogWithData(this.source());
    // Clear URL when modal closes
    this.clearAuthUrl();
  }

  closeModal(): void {
    // Reset all auth state before closing
    this.dialogService.closeTopDialog();
    // Clear URL when modal closes
    this.clearAuthUrl();
  }
  /**
   * Clears the auth URL from address bar when modal is closed
   */
  private clearAuthUrl(): void {
    // Always check the actual browser URL (not router.url) since we use history API
    const browserUrl = globalThis.window?.location.pathname;

    if (!browserUrl) {
      return;
    }

    const postAuthRedirect = this.postAuthRedirect();

    // If we're on an auth route, clear it
    if (includesAuthRoute(browserUrl)) {
      const targetUrl = postAuthRedirect?.url || '/';
      this.router.navigate([targetUrl], { replaceUrl: true }).catch(() => {
        globalThis.window?.history?.replaceState({}, '', targetUrl);
      });
    }
  }

  /**
   * Changes the auth modal state to difference one
   * @param state
   */
  public changeState(state: AuthState): void {
    this.resetMessages();
    this.setStartingSuccessMessage();
    this.selectedState.set(state);
  }

  /**
   * Submits signup OTP for final registration and authenticates
   * @returns
   */
  public submitSignUpOtp(): (
    signupRequestId: string,
    otp: string,
    email: string
  ) => Observable<boolean> {
    return (signupRequestId: string, otp: string) =>
      this.registrationService.verifySignUpOtp(signupRequestId, otp).pipe(
        map((response) => {
          this.resetMessages();
          if (response?.tokenResponse) {
            this.authService.authenticateWithToken(response.tokenResponse);
          }
          return true;
        }),
        tap({
          error: (err) => this.mapValidationError(err),
        })
      );
  }

  /**
   * Regenerates the SignUp OTP
   * @returns
   */
  public regenerateSignUpOTP(): (email: string) => Observable<boolean> {
    return (email: string) =>
      this.registrationService.regenerateSignUpOtp(email).pipe(
        switchMap((response) => {
          this.resetMessages();
          if (response?.message) {
            return from(this.translateService.translate(response.message)).pipe(
              switchMap((translatedMessage) => {
                this.successMessage.set(translatedMessage);
                return of(true);
              })
            );
          } else {
            this.successMessage.set('Check your inbox for an email with your verification code.');
            return of(true);
          }
        }),
        tap({
          error: (err) => this.mapValidationError(err),
        })
      );
  }

  protected mapValidationError(err: unknown): void {
    this.resetMessages();
    if (err instanceof HttpErrorResponse && err.error?.message) {
      this.validationErrorMessage.set(err.error.message);
    }
  }

  /**
   * Resets all the success and validation messages
   */
  public resetMessages(): void {
    this.successMessage.set('');
    this.validationErrorMessage.set('');
  }

  /**
   * Sets the default success value based on current state
   */
  public setStartingSuccessMessage(): void {
    switch (this.selectedState()) {
      case AuthState.VERIFY_ACCOUNT:
      case AuthState.OTP_VERIFICATION:
        this.successMessage.set('Check your inbox for an email with your verification code.');
        break;

      default:
        this.successMessage.set('');
    }
  }

  /**
   * Resend's the OTP to the forgot password flow
   * @returns
   */

  public resendResetPasswordOtp(): (email: string) => Observable<boolean> {
    return (email: string) => {
      return this.passwordService.requestResetPassword(email).pipe(
        switchMap((response) => {
          this.resetMessages();
          // Use the message from API response instead of static message
          if (response?.message) {
            // Translate the API message - convert Promise to Observable
            return from(this.translateService.translate(response.message)).pipe(
              switchMap((translatedMessage) => {
                this.successMessage.set(translatedMessage);
                return of(true);
              })
            );
          } else {
            this.successMessage.set('Check your inbox for an email with your verification code.');
            return of(true);
          }
        }),
        tap({
          error: (err) => this.mapValidationError(err),
        })
      );
    };
  }

  /**
   * Submits the reset password OTP and updates the password
   * @returns
   */
  public verifyResetPasswordOtp(): (
    signupRequestId: string,
    otp: string,
    email: string
  ) => Observable<boolean> {
    return (_: string, otp: string, email: string) => {
      return this.passwordService.verifyOtpForResetPassword(email, otp).pipe(
        map(() => {
          this.resetMessages();
          return true;
        }),
        tap({
          error: (err) => this.mapValidationError(err),
        })
      );
    };
  }
}
